import { useEffect, useState } from 'react';
import { api, track, type EventDetail } from '../api';
import { IconBack, IconCalendar, IconCheck, IconLock, IconPin } from '../components/icons';
import { Footer, TopBar } from '../components/TopBar';
import { navigate } from '../router';
import { backTarget, readLastList } from '../state/url-state';
import { appleMapsUrl, dayLabel, hoursPill, mapsDirectionsUrl, revealLabel, TYPE_LABEL, TYPE_LOOK, whenAt, whenLong } from '../format';
import { useVisitedVersion, visited } from '../state/visited';

export function Listing({ publicId }: { publicId: string }) {
  const [ev, setEv] = useState<EventDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [shared, setShared] = useState(false);
  const [flagMsg, setFlagMsg] = useState('');
  useVisitedVersion();

  useEffect(() => {
    api
      .get<EventDetail>(`/events/${publicId}`)
      .then(setEv)
      .catch(() => setMissing(true));
  }, [publicId]);

  useEffect(() => {
    if (ev) document.title = `${ev.title}, ${ev.city} ${ev.state} · The Yard Sale Map`;
  }, [ev]);

  useEffect(() => {
    if (ev) track('listing_opened', { public_id: ev.public_id, event_type: ev.event_type });
  }, [ev?.public_id]);

  const back = () => {
    if (!ev) return;
    const from = (history.state as { from?: string } | null)?.from;
    const t = backTarget({ from, lastList: readLastList(), lat: ev.lat, lng: ev.lng });
    if (t.action === 'history-back') history.back();
    else navigate(t.to);
  };

  if (missing) {
    return (
      <>
        <TopBar />
        <main className="page" id="main">
          <h1>This listing isn't available</h1>
          <p>It may have ended, been cancelled, or been removed.</p>
          <a className="btn primary" href="/">
            Back to the map
          </a>
        </main>
      </>
    );
  }
  if (!ev) {
    return (
      <>
        <TopBar />
        <main className="page" aria-live="polite">
          <p>Loading…</p>
        </main>
      </>
    );
  }

  const look = TYPE_LOOK[ev.event_type] ?? TYPE_LOOK.other!;
  const full = ev.address_1 ? `${ev.address_1}, ${ev.city}, ${ev.state} ${ev.postal_code}` : '';
  const been = visited.has(ev.public_id);

  async function share() {
    const url = location.href;
    try {
      if (navigator.share) await navigator.share({ title: ev!.title, url });
      else {
        await navigator.clipboard.writeText(url);
        setShared(true);
      }
    } catch {
      /* dismissed */
    }
  }

  async function flag() {
    setFlagMsg('Thanks — reported.');
    try {
      await api.postJson(`/events/${publicId}/flag`, {});
    } catch {
      /* the message already shows regardless -- this is a soft, best-effort report,
       * same convention as PlaceListing.tsx's flag() */
    }
  }

  return (
    <>
      <TopBar />
      <main className="listing" id="main">
        <div className="hero">
          {ev.photos.length ? (
            <div className="strip" tabIndex={0} role="region" aria-label={`Photos of ${ev.title}, ${ev.photos.length} total`}>
              {ev.photos.map((p, i) => (
                <img key={p.url} src={p.url} width={p.width} height={p.height} alt={`Photo ${i + 1} of ${ev.photos.length}: ${ev.title}`} loading={i ? 'lazy' : 'eager'} />
              ))}
            </div>
          ) : (
            <div className="placeholder" style={{ background: look.bg }} aria-hidden="true">
              {look.glyph}
            </div>
          )}
          <button type="button" className="back" onClick={back}>
            <IconBack />
            <span>Back to list</span>
          </button>
          {ev.photos.length > 1 && <span className="count">{ev.photos.length} photos, swipe to see more</span>}
        </div>

        {ev.status === 'expired' && <p className="banner-ended">This listing has ended.</p>}

        <div className="listing-body">
          <span className="tag">{TYPE_LABEL[ev.event_type] ?? 'Sale'}</span>
          <h1>{ev.title}</h1>
          <div className="listing-sub">
            {ev.city}, {ev.state} {ev.postal_code}
          </div>

          <div className="facts">
            <span className="ic">
              <IconCalendar />
            </span>
            <div>
              <b>{whenLong(ev.start_at, ev.end_at, ev.timezone, ev.end_time_unknown)}</b>
              {ev.recurrence_text && <span>Repeats: {ev.recurrence_text}</span>}
            </div>
            {!ev.is_online_only && (
              <>
                <span className="ic">{ev.approximate ? <IconLock /> : <IconPin />}</span>
                {ev.approximate ? (
                  <div>
                    <b>Address posted {ev.address_reveal_at ? revealLabel(ev.address_reveal_at, ev.timezone) : 'soon'}</b>
                    <span>Until then the map shows the approximate area near {ev.city}.</span>
                  </div>
                ) : (
                  <div>
                    <b>
                      {ev.venue_name ? `${ev.venue_name}, ` : ''}
                      {[ev.address_1, ev.address_2].filter(Boolean).join(', ')}
                    </b>
                    <span>
                      {ev.city}, {ev.state} {ev.postal_code}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="actions">
            {!ev.is_online_only && !ev.approximate ? (
              <a className="btn primary" href={mapsDirectionsUrl(full)} target="_blank" rel="noopener">
                Directions
              </a>
            ) : ev.external_url ? (
              <a className="btn primary" href={ev.external_url} target="_blank" rel="noopener nofollow ugc">
                View online listing
              </a>
            ) : (
              <button type="button" disabled>
                Directions
              </button>
            )}
            <button type="button" onClick={() => void share()}>
              {shared ? 'Link copied' : 'Share'}
            </button>
            <button type="button" aria-pressed={been} onClick={() => visited.toggle(ev.public_id)}>
              {been && <IconCheck />}
              {been ? 'Been there' : 'Mark visited'}
            </button>
          </div>
          {!ev.is_online_only && !ev.approximate && (
            <p className="hint">
              Prefer Apple Maps?{' '}
              <a href={appleMapsUrl(full)} target="_blank" rel="noopener">
                Open it there
              </a>
              .{ev.external_url && (
                <>
                  {' '}
                  Also listed{' '}
                  <a href={ev.external_url} target="_blank" rel="noopener nofollow ugc">
                    online
                  </a>
                  .
                </>
              )}
            </p>
          )}

          {ev.special_instructions && <div className="note">{ev.special_instructions}</div>}

          <div className="description">
            {ev.description.split(/\n{2,}/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          {ev.auction && (
            <section style={{ marginTop: 20 }} aria-label="Auction details">
              <h2 style={{ fontSize: '1rem' }}>Auction details</h2>
              {ev.auction.format === 'online' && (
                <div className="note">
                  This is an <b>online-only</b> auction. There's nowhere to drive to, so it isn't on the map or in route planning.
                </div>
              )}
              <ul className="upcoming">
                <li>
                  <b>Bidding:</b>{' '}
                  {ev.auction.format === 'in_person'
                    ? 'In person'
                    : ev.auction.format === 'online'
                      ? 'Online only'
                      : 'Online, with an in-person preview or pickup'}
                </li>
                {ev.auction.auctioneer_name && (
                  <li>
                    <b>Auctioneer:</b> {ev.auction.auctioneer_name}
                  </li>
                )}
                {ev.auction.auction_start_at && (
                  <li>
                    <b>Starts:</b> {whenAt(ev.auction.auction_start_at, ev.timezone)}
                  </li>
                )}
                {ev.auction.preview_start_at && (
                  <li>
                    <b>Preview:</b>{' '}
                    {ev.auction.preview_end_at ? whenLong(ev.auction.preview_start_at, ev.auction.preview_end_at, ev.timezone) : whenAt(ev.auction.preview_start_at, ev.timezone)}
                  </li>
                )}
                {ev.auction.pickup_info && (
                  <li>
                    <b>Pickup:</b> {ev.auction.pickup_info}
                  </li>
                )}
                {ev.auction.catalog_url && (
                  <li>
                    <a href={ev.auction.catalog_url} target="_blank" rel="noopener nofollow ugc">
                      Browse the catalog
                    </a>
                  </li>
                )}
                {ev.auction.terms_url && (
                  <li>
                    <a href={ev.auction.terms_url} target="_blank" rel="noopener nofollow ugc">
                      Terms and conditions
                    </a>
                  </li>
                )}
              </ul>
            </section>
          )}

          {ev.upcoming.length > 1 && (
            <section style={{ marginTop: 20 }}>
              <h2 style={{ fontSize: '1rem' }}>Upcoming dates</h2>
              <ul className="upcoming">
                {ev.upcoming.map((o) => (
                  <li key={o.start_at}>
                    {dayLabel(o.start_at, ev.timezone)}, {hoursPill(o.start_at, o.end_at, ev.timezone, o.end_time_unknown)}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className="hint" style={{ marginTop: 28 }}>
            {flagMsg || (
              <>
                Something wrong with this listing?{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    void flag();
                  }}
                >
                  Let us know
                </a>
                .
              </>
            )}
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}
